import type { ReactElement } from 'react';
import { Unlink } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DataTable,
  DataTableHeaderRow,
  DataTableTable,
  DataTableTbody,
  DataTableTd,
  DataTableTh,
  DataTableThead,
  DataTableTr,
} from '@/components/ui/data-table';
import { FetchErrorCallout } from '@/components/ui/fetch-error-callout';
import { Skeleton } from '@/components/ui/skeleton';

import type { FederatedIdentityViewModel } from '../api';

/**
 * A user's linked federated identities (PTV2-1450). Purely presentational — the host page supplies the
 * data + the unlink handler — so the same panel serves the global-plane user page now and the tenant
 * user page later without change. Rendered as a self-contained section (heading + table/states).
 */
/** The full data + handlers a host supplies to render the panel (so it can be passed as one bundle). */
export interface LinkedIdentitiesPanelData {
  readonly identities: readonly FederatedIdentityViewModel[];
  readonly isLoading: boolean;
  readonly isError: boolean;
  readonly error: unknown;
  readonly onRetry: () => void;
  readonly onUnlink: (identity: FederatedIdentityViewModel) => void;
  readonly pendingUnlinkGuid: string | null;
}

export function LinkedIdentitiesPanel({
  identities,
  isLoading,
  isError,
  error,
  onRetry,
  onUnlink,
  pendingUnlinkGuid,
}: LinkedIdentitiesPanelData): ReactElement {
  const { t } = useTranslation();
  const fp = 'linkedIdentities';

  return (
    <section aria-labelledby="linked-identities-heading" className="space-y-3">
      <div>
        <h2 id="linked-identities-heading" className="text-base font-semibold text-foreground">
          {t(`${fp}.heading`)}
        </h2>
        <p className="mt-1 text-sm text-muted-foreground">{t(`${fp}.subheading`)}</p>
      </div>

      {isLoading && (
        <div className="space-y-2" aria-hidden="true">
          {Array.from({ length: 2 }).map((_, i) => (
            <Skeleton key={i} className="h-9 w-full rounded-md" />
          ))}
        </div>
      )}

      {isError && (
        <div className="max-w-xl">
          <FetchErrorCallout
            error={error}
            heading={t(`${fp}.error.heading`)}
            message={t(`${fp}.error.message`)}
            codeLabel={t(`${fp}.error.codeLabel`)}
            onRetry={onRetry}
            retryLabel={t(`${fp}.error.retry`)}
          />
        </div>
      )}

      {!isLoading && !isError && identities.length === 0 && (
        <p className="text-sm text-muted-foreground" role="status">
          {t(`${fp}.empty`)}
        </p>
      )}

      {!isLoading && !isError && identities.length > 0 && (
        <DataTable aria-label={t(`${fp}.heading`)}>
          <DataTableTable aria-label={t(`${fp}.heading`)}>
            <DataTableThead className="sticky top-0 z-10 bg-card">
              <DataTableHeaderRow>
                <DataTableTh className="h-[40px] py-0">{t(`${fp}.table.provider`)}</DataTableTh>
                <DataTableTh className="h-[40px] py-0">{t(`${fp}.table.subject`)}</DataTableTh>
                <DataTableTh className="h-[40px] py-0" widthClassName="w-[7rem]">
                  {t(`${fp}.table.status`)}
                </DataTableTh>
                <DataTableTh className="h-[40px] py-0" widthClassName="w-[6rem]">
                  {t(`${fp}.table.actions`)}
                </DataTableTh>
              </DataTableHeaderRow>
            </DataTableThead>
            <DataTableTbody>
              {identities.map((identity) => (
                <DataTableTr key={identity.guid} className="even:bg-muted/55">
                  <DataTableTd
                    className="h-[40px] py-0 font-mono text-xs text-muted-foreground"
                    dataOffset
                  >
                    <span className="block truncate">{identity.providerIssuer}</span>
                  </DataTableTd>
                  <DataTableTd
                    className="h-[40px] py-0 font-mono text-xs text-muted-foreground"
                    dataOffset={false}
                  >
                    <span className="block truncate" title={identity.externalSubject}>
                      {identity.externalSubject}
                    </span>
                  </DataTableTd>
                  <DataTableTd className="h-[40px] py-0" dataOffset={false}>
                    <Badge variant={identity.status === 'ACTIVE' ? 'default' : 'outline'}>
                      {t(`${fp}.statusLabel.${identity.status}`)}
                    </Badge>
                  </DataTableTd>
                  <DataTableTd className="h-[40px] py-0" dataOffset={false}>
                    {identity.status === 'RETIRED' ? (
                      <span className="text-xs text-muted-foreground">{t(`${fp}.table.noActions`)}</span>
                    ) : (
                      <Button
                        size="icon"
                        variant="ghost"
                        aria-label={t(`${fp}.unlinkAction`)}
                        title={t(`${fp}.unlinkAction`)}
                        loading={pendingUnlinkGuid === identity.guid}
                        onClick={() => {
                          onUnlink(identity);
                        }}
                      >
                        <Unlink className="h-4 w-4" aria-hidden="true" />
                      </Button>
                    )}
                  </DataTableTd>
                </DataTableTr>
              ))}
            </DataTableTbody>
          </DataTableTable>
        </DataTable>
      )}
    </section>
  );
}
